import { Button, LoadingOverlay, Select, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import type { FormValueTypes } from '~/common/notifications/types';
import { getNotificationConfigValues } from '~/common/notifications/types';
import { api } from '~/trpc-extra/api';
import { NotificationSubForm } from './NotificationsSubForm';
import { notifications } from '@mantine/notifications';
import { useDeepCompareEffect } from '@react-hookz/web';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useSlack } from '~/hooks/slack/useSlack';
import { SlackHookUpdater } from '~/components/auth/SlackHookUpdater';

export default function NotificationsFormSlack({
  projectId,
  testSuiteId,
}: {
  projectId: string;
  testSuiteId?: string;
}) {
  const trpcUtils = api.useUtils();

  const { data: slackConfigs, isLoading } = api.project.listSlackConfigs.useQuery(
    { projectId, testSuiteId },
    { refetchOnWindowFocus: false },
  );
  const { mutateAsync: deleteSlackConfig, isPending: isDeleting } = api.project.deleteSlackConfig.useMutation({
    onSuccess: async () => {
      await trpcUtils.project.listSlackConfigs.invalidate();
      notifications.show({
        message: 'Slack notification config deleted',
      });
    },
  });
  const { mutateAsync: saveSlackConfig, isPending: isSaving } = api.project.upsertSlackConfig.useMutation();

  const initChannel = slackConfigs?.at(0);
  const rawSlackConfig = initChannel?.incomingWebhook?.SlackNotificationConfig;
  const slackConfig = getNotificationConfigValues(
    rawSlackConfig ? { ...rawSlackConfig, dailySummary: rawSlackConfig.dailySummary ?? false } : rawSlackConfig,
    { channel: initChannel?.incomingWebhook?.url || '' },
  );
  const { connectSlack } = useSlack();

  const form = useForm<FormValueTypes>({
    initialValues: {
      channel: '',
      testRunStarts: false,
      testRunFinishes: false,
      weeklySummary: false,
      dailySummary: false,
      endType: 'NONE',
      disableOverride: false,
    },
  });

  useDeepCompareEffect(() => {
    form.setValues(slackConfig);
  }, [slackConfig]);

  return (
    <>
      <LoadingOverlay
        visible={isLoading || isSaving}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
      <form
        onSubmit={form.onSubmit(async (values) => {
          try {
            await saveSlackConfig({
              url: values.channel || '',
              testSuiteRunStart: values.testRunStarts,
              testSuiteRunEnd:
                values.testRunFinishes ?
                  values.endType === 'NONE' ?
                    'ALL_RESULTS'
                  : values.endType
                : 'NONE',
              weeklySummary: values.weeklySummary,
              dailySummary: values.dailySummary,
              projectId,
            });
            await trpcUtils.project.listSlackConfigs.invalidate();
            notifications.show({
              message: 'Slack notification config saved',
            });
            modals.closeAll();
          } catch (e) {
            console.error('Error saving notifications:', e);
            notifications.show({
              color: 'red',
              message: 'Failed to save slack notification config',
            });
          }
        })}
      >
        <SlackHookUpdater
          projectId={projectId}
          testSuiteId={testSuiteId}
        />
        <div className="text-lg font-medium">Slack notifications</div>
        <div className="flex items-center gap-2">
          <Select
            required
            className="mt-2 flex-1"
            {...form.getInputProps('channel')}
            onChange={(value) => {
              const selectedChannel = slackConfigs?.find((channel) => channel.incomingWebhook?.url === value);
              const config = selectedChannel?.incomingWebhook?.SlackNotificationConfig;
              form.setValues(
                getNotificationConfigValues(
                  config ? { ...config, dailySummary: config.dailySummary ?? false } : config,
                  { channel: value || '' },
                ),
              );
            }}
            allowDeselect={false}
            data={
              slackConfigs?.map((channel) => ({
                value: channel.incomingWebhook?.url || '',
                label: channel.incomingWebhook?.channel || '',
              })) || []
            }
          />
          <Button
            variant="subtle"
            className="mt-2"
            leftSection={<IconPlus size={16} />}
            onClick={async () => {
              // We don't check for testSuiteId because `testSuiteId = undefined` is
              // a valid option (project-level notification setting)
              if (projectId) {
                await connectSlack(projectId, testSuiteId).catch(() => undefined);
              }
            }}
          >
            Add Channel
          </Button>
          <Tooltip
            label="At least one Slack channel must remain connected"
            disabled={!!slackConfigs && slackConfigs.length > 1}
          >
            <Button
              variant="subtle"
              color="red"
              className="mt-2"
              leftSection={<IconTrash size={16} />}
              disabled={slackConfigs?.length === 1}
              loading={isDeleting}
              onClick={async () => {
                const selectedChannel = slackConfigs?.find(
                  (channel) => channel.incomingWebhook?.url === form.values.channel,
                );
                if (selectedChannel) {
                  await deleteSlackConfig({
                    projectId,
                    testSuiteId,
                    url: selectedChannel?.incomingWebhook?.url ?? '',
                  });

                  // Update the form so we don't keep selecting the deleted channel
                  const newSelectedChannel = slackConfigs?.at(0);
                  const newConfig = newSelectedChannel?.incomingWebhook?.SlackNotificationConfig;
                  form.setValues(
                    getNotificationConfigValues(
                      newConfig ? { ...newConfig, dailySummary: newConfig.dailySummary ?? false } : newConfig,
                      {
                        channel: newSelectedChannel?.incomingWebhook?.url || '',
                      },
                    ),
                  );
                }
              }}
            >
              Delete Channel
            </Button>
          </Tooltip>
        </div>

        <NotificationSubForm
          form={form}
          showWeeklySummaryField={!testSuiteId}
        />

        <div className="mt-8 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => modals.closeAll()}
          >
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </>
  );
}
